import { MultiSelect } from '@carbon/react';
import classNames from 'classnames';
import type { ProductionDataSource } from 'types/api-responses';
import {
  PRODUCTION_DATA_SOURCE_LABELS,
  PRODUCTION_DATA_SOURCES,
} from './utils';
import style from './production-source-dropdown.module.scss';

interface ProductionSourceMultiSelectProps {
  /** The selected sources, in the order they were picked. The first is the one the figures follow. */
  value: ProductionDataSource[];
  /** The sources the well has production for; the rest are disabled. */
  availableSources: ProductionDataSource[];
  /** Called with the new selection. It is never called with an empty one. */
  onChange: (sources: ProductionDataSource[]) => void;
  /** Placement from the caller, such as margins. */
  className?: string;
}

/**
 * Picks one or more production data sources to show a well's production from.
 * Sources the well has no production for are listed but disabled, and the last
 * selected source can't be cleared. The selected sources are named in the
 * order they were picked.
 *
 * @param {ProductionSourceMultiSelectProps} props - The selection, the sources the well has, the change handler and the caller's class.
 * @return {JSX.Element} The multi-select.
 */
export function ProductionSourceMultiSelect({
  value,
  availableSources,
  onChange,
  className,
}: ProductionSourceMultiSelectProps) {
  const items = PRODUCTION_DATA_SOURCES.map((source) => ({
    id: source,
    label: PRODUCTION_DATA_SOURCE_LABELS[source],
    disabled: !availableSources.includes(source),
  }));
  const selectedItems = items.filter((item) => value.includes(item.id));

  return (
    <div className={classNames(style.sourceMultiSelect, className)}>
      <MultiSelect
        id="production-data-sources"
        titleText="Production Data Source"
        label={value
          .map((source) => PRODUCTION_DATA_SOURCE_LABELS[source])
          .join(', ')}
        size="lg"
        items={items}
        itemToString={(item) => item?.label ?? ''}
        selectedItems={selectedItems}
        onChange={({ selectedItems: selected }) => {
          if (selected && selected.length > 0)
            onChange(selected.map((item) => item.id));
        }}
        selectionFeedback="fixed"
        sortItems={(sortable) => [...sortable]}
      />
    </div>
  );
}
